import { useMemo } from "react";
import { BreakevenChart } from "../components/fiscal/BreakevenChart.js";
import { FiscalYearSelector } from "../components/fiscal/FiscalYearSelector.js";
import { KpiCard } from "../components/fiscal/KpiCard.js";
import { FiscalCard, FiscalShell, LoadingSkeleton } from "../components/fiscal/Shell.js";
import { useFiscalSelection } from "../hooks/useFiscalSelection.js";
import { useT } from "../i18n/index.js";
import { computeEcartSeuil } from "../lib/fiscal-derived.js";
import { formatDays, formatMoney, formatPct, toNumber } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";

export default function FiscalSeuil() {
  const { companyId, fiscalYearId, setCompanyId, setFiscalYearId } = useFiscalSelection();
  const t = useT();

  const calcQuery = trpc.fiscalCalculations.getByFiscalYear.useQuery(
    { companyId: companyId ?? 0, fiscalYearId: fiscalYearId ?? 0 },
    { enabled: companyId !== null && fiscalYearId !== null },
  );

  const calc = calcQuery.data;

  const ecart = useMemo(
    () =>
      calc
        ? computeEcartSeuil({
            caHt: calc.caHt,
            seuilEconomique: calc.seuilEconomique,
          })
        : null,
    [calc],
  );

  const toolbar = (
    <FiscalYearSelector
      companyId={companyId}
      fiscalYearId={fiscalYearId}
      onChangeCompany={setCompanyId}
      onChangeFiscalYear={(fy) => setFiscalYearId(fy?.id ?? null)}
    />
  );

  return (
    <FiscalShell subtitle={t("fiscalSeuil.subtitle")} toolbar={toolbar}>
      {(companyId === null || fiscalYearId === null) && (
        <FiscalCard>
          <p className="text-sm text-neutral-400">{t("fiscalSeuil.selectCompanyFy")}</p>
        </FiscalCard>
      )}
      {fiscalYearId !== null && calcQuery.isLoading && (
        <FiscalCard>
          <LoadingSkeleton rows={6} />
        </FiscalCard>
      )}
      {fiscalYearId !== null && !calcQuery.isLoading && !calc && (
        <FiscalCard title={t("fiscalSeuil.noKpisTitle")}>
          <p className="text-sm text-neutral-400">{t("fiscalSeuil.noKpisHint")}</p>
        </FiscalCard>
      )}
      {calc && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              label={t("fiscalSeuil.seuilEconomique")}
              value={formatMoney(calc.seuilEconomique)}
              tooltip={t("fiscalSeuil.seuilEconomiqueTooltip")}
            />
            <KpiCard
              label={t("fiscalSeuil.seuilFinancier")}
              value={formatMoney(calc.seuilFinancier)}
              tooltip={t("fiscalSeuil.seuilFinancierTooltip")}
              subtitle={t("fiscalSeuil.seuilFinancierSubtitle")}
            />
            <KpiCard
              label={t("fiscalSeuil.ecartSeuil")}
              value={formatMoney(ecart?.ecart ?? null)}
              secondaryLabel={t("fiscalSeuil.pctOfCa")}
              secondaryValue={formatPct(ecart?.ecartPctOfCa, { alreadyPct: true })}
              tone={
                ecart?.ecartPctOfCa === null
                  ? "neutral"
                  : (ecart?.ecartPctOfCa ?? 100) < 10
                    ? "amber"
                    : (ecart?.ecart ?? 0) < 0
                      ? "red"
                      : "positive"
              }
              tooltip={t("fiscalSeuil.ecartSeuilTooltip")}
            />
            <KpiCard
              label={t("fiscalSeuil.pointMort")}
              value={formatDays(calc.pointMortJours)}
              subtitle={t("fiscalSeuil.pointMortSubtitle")}
              tooltip={t("fiscalSeuil.pointMortTooltip")}
            />
          </div>

          <FiscalCard
            title={t("fiscalSeuil.componentsTitle")}
            subtitle={t("fiscalSeuil.componentsSubtitle")}
          >
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-y-3 gap-x-6 text-sm">
              <Item label={t("fiscalSeuil.chargesVariables")} value={calc.chargesVariablesTotal} />
              <Item label={t("fiscalSeuil.chargesFixes")} value={calc.chargesFixesTotal} />
              <Item
                label={t("fiscalSeuil.chargesFixesSeuilFin")}
                value={calc.chargesFixesSeuilFin}
              />
              <Item label={t("fiscalSeuil.margeCv")} value={calc.margeCv} />
              <div>
                <dt className="text-xs uppercase tracking-wider text-neutral-500">
                  {t("fiscalSeuil.tauxMargeCv")}
                </dt>
                <dd className="font-mono">{formatPct(calc.tauxMargeCv, { alreadyPct: true })}</dd>
              </div>
              <Item label={t("fiscalSeuil.caHt")} value={calc.caHt} />
              <Item label={t("fiscalSeuil.autresChargesVar")} value={calc.autresChargesVariables} />
              <Item label={t("fiscalSeuil.achatsConsommes")} value={calc.achatsConsommes} />
            </dl>
          </FiscalCard>

          <FiscalCard
            title={t("fiscalSeuil.breakevenChartTitle")}
            subtitle={t("fiscalSeuil.breakevenChartSubtitle")}
          >
            <BreakevenChart
              caHt={toNumber(calc.caHt)}
              seuilEconomique={toNumber(calc.seuilEconomique)}
              chargesFixes={toNumber(calc.chargesFixesTotal)}
              tauxMargeCv={toNumber(calc.tauxMargeCv)}
            />
          </FiscalCard>
        </>
      )}
    </FiscalShell>
  );
}

function Item({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="font-mono">{formatMoney(value)}</dd>
    </div>
  );
}
